import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { NotFound } from '../pages/NotFound';
import { useAuth } from '@features/auth';
import type { UserRole } from '@features/auth';
import { ErrorBoundary } from '@/shared/components/ErrorBoundary';
import { PageTransition } from '@/shared/components/PageTransition';

// Lazy loading para mejor performance
const LoginContainer = lazy(() =>
  import('@features/auth').then(module => ({ default: module.LoginContainer }))
);

const RegisterContainer = lazy(() =>
  import('@features/auth').then(module => ({ default: module.RegisterContainer }))
);

const RegisterSelectorContainer = lazy(() =>
  import('@features/auth').then(module => ({ default: module.RegisterSelectorContainer }))
);

const BusinessRegisterContainer = lazy(() =>
  import('@features/auth').then(module => ({ default: module.BusinessRegisterContainer }))
);

const EntrepreneurRegisterContainer = lazy(() =>
  import('@features/auth').then(module => ({ default: module.EntrepreneurRegisterContainer }))
);

const DashboardContainer = lazy(() =>
  import('@features/dashboard').then(module => ({ default: module.DashboardContainer }))
);

const AllActivitiesContainer = lazy(() =>
  import('@features/dashboard').then(module => ({ default: module.AllActivitiesContainer }))
);

const AcademicStructureWrapper = lazy(() =>
  import('@features/academic-structure').then(module => ({ default: module.AcademicStructureWrapper }))
);

const TeacherSubjectsContainer = lazy(() =>
  import('@features/academic-structure').then(module => ({ default: module.TeacherSubjectsContainer }))
);

const CourseManagementContainer = lazy(() =>
  import('@features/academic-structure').then(module => ({ default: module.CourseManagementContainer }))
);

const EnrollmentContainer = lazy(() =>
  import('@features/enrollment').then(module => ({ default: module.EnrollmentContainer }))
);

const ScheduleContainer = lazy(() =>
  import('@features/schedule').then(module => ({ default: module.ScheduleContainer }))
);

const LMSContainer = lazy(() =>
  import('@features/lms').then(module => ({ default: module.LMSContainer }))
);

const VideoLessonContainer = lazy(() =>
  import('@features/lms').then(module => ({ default: module.VideoLessonContainer }))
);

const AssessmentContainer = lazy(() =>
  import('@features/lms').then(module => ({ default: module.AssessmentContainer }))
);

const EssaySubmissionContainer = lazy(() =>
  import('@features/lms').then(module => ({ default: module.EssaySubmissionContainer }))
);

const ExamBuilderContainer = lazy(() =>
  import('@features/lms').then(module => ({ default: module.ExamBuilderContainer }))
);

const GradeAssessmentContainer = lazy(() =>
  import('@features/lms').then(module => ({ default: module.GradeAssessmentContainer }))
);

const VideoManagementContainer = lazy(() =>
  import('@features/video-management').then(module => ({ default: module.VideoManagementContainer }))
);

const VideoDetailView = lazy(() =>
  import('@features/video-management').then(module => ({ default: module.VideoDetailView }))
);

const NotificationsContainer = lazy(() =>
  import('@features/notifications').then(module => ({ default: module.NotificationsContainer }))
);

const NotificationCenterContainer = lazy(() =>
  import('@features/notifications').then(module => ({ default: module.NotificationCenterContainer }))
);

const StatisticsContainer = lazy(() =>
  import('@features/statistics').then(module => ({ default: module.StatisticsContainer }))
);

const ForumContainer = lazy(() =>
  import('@features/forum').then(module => ({ default: module.ForumContainer }))
);

const MessagingContainer = lazy(() =>
  import('@features/messaging').then(module => ({ default: module.MessagingContainer }))
);

const JoinLevelContainer = lazy(() =>
  import('@features/academic-structure').then(module => ({ default: module.JoinLevelContainer }))
);

const ProfileContainer = lazy(() =>
  import('@features/profile').then(module => ({ default: module.ProfileContainer }))
);

const SettingsContainer = lazy(() =>
  import('@features/settings').then(module => ({ default: module.SettingsContainer }))
);

const PlatformSettingsContainer = lazy(() =>
  import('@features/settings').then(module => ({ default: module.PlatformSettingsContainer }))
);

const CustomizationSettingsContainer = lazy(() =>
  import('@features/settings').then(module => ({ default: module.CustomizationSettingsContainer }))
);

const UserManagementContainer = lazy(() =>
  import('@features/settings').then(module => ({ default: module.UserManagementContainer }))
);

const StudentCertificatesContainer = lazy(() =>
  import('@features/certificates/presentation/containers').then(module => ({ default: module.StudentCertificatesContainer }))
);

const PublicVerificationContainer = lazy(() =>
  import('@features/certificates/presentation/containers').then(module => ({ default: module.PublicVerificationContainer }))
);

const MagicLinkRedirect = lazy(() =>
  import('@features/settings/presentation/components/MagicLinkRedirect').then(module => ({ default: module.MagicLinkRedirect }))
);

const CertificateEditorContainer = lazy(() =>
  import('@features/certificates/presentation/containers').then(module => ({ default: module.CertificateEditorContainer }))
);

const WorkspaceManagementContainer = lazy(() =>
  import('@features/workspace-management').then(module => ({ default: module.WorkspaceManagementContainer }))
);

const WorkspaceDetailContainer = lazy(() =>
  import('@features/workspace-management').then(module => ({ default: module.WorkspaceDetailContainer }))
);

const CronJobsContainer = lazy(() =>
  import('@features/workspace-management').then(module => ({ default: module.CronJobsContainer }))
);

const ClientManagementContainer = lazy(() =>
  import('@features/client-management').then(module => ({ default: module.ClientManagementContainer }))
);

const SubscriptionPlansContainer = lazy(() =>
  import('@features/subscription-plans').then(module => ({ default: module.SubscriptionPlansContainer }))
);

const DirectorSubscriptionContainer = lazy(() =>
  import('@features/subscription-plans').then(module => ({ default: module.DirectorSubscriptionContainer }))
);

const PaymentDashboardContainer = lazy(() =>
  import('@features/payment-management').then(module => ({ default: module.PaymentDashboardContainer }))
);

const PaymentMethodConfigContainer = lazy(() =>
  import('@features/payment-management').then(module => ({ default: module.PaymentMethodConfigContainer }))
);

const UpgradeRequestsContainer = lazy(() =>
  import('@features/payment-management').then(module => ({ default: module.UpgradeRequestsContainer }))
);

const SubscriptionsContainer = lazy(() =>
  import('@features/payment-management').then(module => ({ default: module.SubscriptionsContainer }))
);

const VersionHistoryContainer = lazy(() =>
  import('@features/version-history').then(module => ({ default: module.VersionHistoryContainer }))
);

const EmailManagementContainer = lazy(() =>
  import('@features/email-management').then(module => ({ default: module.EmailManagementContainer }))
);

const MuxWebhooksContainer = lazy(() =>
  import('@features/mux-webhooks').then(module => ({ default: module.MuxWebhooksContainer }))
);

const AdminVideoAnalyticsContainer = lazy(() =>
  import('@features/video-statistics').then(module => ({ default: module.AdminVideoAnalyticsContainer }))
);

const WorkspaceAlertsContainer = lazy(() =>
  import('@features/workspace-alerts').then(module => ({ default: module.WorkspaceAlertsContainer }))
);

const AppConfigContainer = lazy(() =>
  import('@features/app-config').then(module => ({ default: module.AppConfigContainer }))
);

const LogoGeneratorContainer = lazy(() =>
  import('@features/logo-generator/presentation').then(module => ({ default: module.LogoGeneratorContainer }))
);

const FeatureFlagContainer = lazy(() =>
  import('@features/feature-flags').then(module => ({ default: module.FeatureFlagContainer }))
);

const SystemArchitectureContainer = lazy(() =>
  import('@features/system-architecture').then(module => ({ default: module.SystemArchitectureContainer }))
);

const FlashcardContainer = lazy(() =>
  import('@features/flashcards').then(module => ({ default: module.FlashcardContainer }))
);

const FlashcardAnalyticsContainer = lazy(() =>
  import('@features/flashcards').then(module => ({ default: module.FlashcardAnalyticsContainer }))
);

const RankingDashboardContainer = lazy(() =>
  import('@features/ranking').then(module => ({ default: module.RankingDashboardContainer }))
);

const HerramientasContainer = lazy(() =>
  import('@features/admin-hub').then(module => ({ default: module.HerramientasContainer }))
);

// Loading component para lazy loading
function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FB]">
      <div className="text-center">
        <div className="w-16 h-16 border-4 border-[var(--brand-primary)] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-gray-600">Cargando...</p>
      </div>
    </div>
  );
}

// Componente para redirección raíz
function RootRedirect() {
  const { isAuthenticated, isLoading, user } = useAuth();

  if (isLoading) {
    return <LoadingFallback />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Estudiantes van directo al LMS (su objetivo principal es aprender)
  // Admin va directo a pagos (su objetivo principal es gestión de plataforma)
  // Otros roles van al dashboard (necesitan ver estadísticas/gestión)
  const targetRoute =
    user?.role === 'student' ? '/lms' :
    user?.role === 'admin' ? '/admin/pagos' :
    '/dashboard';
  return <Navigate to={targetRoute} replace />;
}

export function AppRoutes() {
  const { user } = useAuth();

  return (
    <Suspense fallback={<LoadingFallback />}>
      <PageTransition>
      <Routes>
        {/* Root redirect */}
        <Route path="/" element={<RootRedirect />} />

        {/* Public routes */}
        <Route path="/login" element={<LoginContainer onShowRegister={() => {}} />} />

        {/* Registration routes */}
        <Route path="/register" element={<RegisterSelectorContainer />} />
        <Route path="/register/student" element={<RegisterContainer onBackToLogin={() => {}} />} />
        <Route path="/register/business" element={<BusinessRegisterContainer />} />
        <Route path="/register/entrepreneur" element={<EntrepreneurRegisterContainer />} />

        <Route path="/join/:token" element={<JoinLevelContainer />} />
        <Route path="/verificar/:verificationCode" element={<PublicVerificationContainer />} />
        <Route path="/m/:code" element={<MagicLinkRedirect />} />

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ErrorBoundary featureName="Dashboard">
                <DashboardContainer userRole={user?.role || 'student'} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/dashboard/actividades"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher'] as UserRole[]}>
              <ErrorBoundary featureName="Actividades">
                <AllActivitiesContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/estructura"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Estructura Académica">
                <AcademicStructureWrapper userRole={user?.role || 'director'} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/mis-materias"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher'] as UserRole[]}>
              <TeacherSubjectsContainer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/estructura/curso/:courseId"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher'] as UserRole[]}>
              <CourseManagementContainer />
            </ProtectedRoute>
          }
        />

        {/* Director & Teacher: Gestión de Calificaciones */}
        <Route
          path="/calificaciones"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher'] as UserRole[]}>
              <ErrorBoundary featureName="Inscripciones">
                <EnrollmentContainer userRole={user?.role || 'director'} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Student: Kardex / Inscripciones */}
        <Route
          path="/inscripciones"
          element={
            <ProtectedRoute allowedRoles={['student'] as UserRole[]}>
              <ErrorBoundary featureName="Inscripciones">
                <EnrollmentContainer userRole={user?.role || 'student'} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Student: Certificates */}
        <Route
          path="/mis-certificados"
          element={
            <ProtectedRoute allowedRoles={['student'] as UserRole[]}>
              <ErrorBoundary featureName="Certificados">
                <StudentCertificatesContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Student: Achievements - Redirect old route to profile tab */}
        <Route
          path="/mis-logros"
          element={<Navigate to="/perfil/logros" replace />}
        />

        <Route
          path="/horarios"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ErrorBoundary featureName="Horarios">
                <ScheduleContainer userRole={user?.role || 'student'} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lms"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ErrorBoundary featureName="LMS">
                <LMSContainer userRole={user?.role || 'student'} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lms/lesson/:lessonId"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ErrorBoundary featureName="LMS">
                <VideoLessonContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lms/assessment/:evaluationId"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ErrorBoundary featureName="LMS">
                <AssessmentContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lms/essay/:evaluationId"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ErrorBoundary featureName="LMS">
                <EssaySubmissionContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lms/exam-builder/:evaluationId"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher'] as UserRole[]}>
              <ErrorBoundary featureName="LMS">
                <ExamBuilderContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/lms/grade/:attemptId"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher'] as UserRole[]}>
              <ErrorBoundary featureName="LMS">
                <GradeAssessmentContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Video Management - Directors only */}
        <Route
          path="/gestion-videos"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Gestión de Videos">
                <VideoManagementContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gestion-videos/:videoId"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Gestión de Videos">
                <VideoDetailView />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/forum"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ErrorBoundary featureName="Foro">
                <ForumContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Flashcards */}
        <Route
          path="/flashcards"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ErrorBoundary featureName="Flashcards">
                <FlashcardContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Direct Messaging */}
        <Route
          path="/mensajes"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <MessagingContainer />
            </ProtectedRoute>
          }
        />

        {/* Ranking - Students, Teachers, Directors */}
        <Route
          path="/ranking"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <RankingDashboardContainer />
            </ProtectedRoute>
          }
        />

        {/* Suggestions - Inside profile as tab */}
        <Route
          path="/perfil/sugerencias"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ProfileContainer />
            </ProtectedRoute>
          }
        />

        {/* Achievements - Inside profile as tab (students only) */}
        <Route
          path="/perfil/logros"
          element={
            <ProtectedRoute allowedRoles={['student'] as UserRole[]}>
              <ProfileContainer />
            </ProtectedRoute>
          }
        />

        {/* Notification Center - All roles (view notifications) */}
        <Route
          path="/notificaciones"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student', 'admin'] as UserRole[]}>
              <ErrorBoundary featureName="Notificaciones">
                <NotificationCenterContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Notification Management - Directors only (send notifications) */}
        <Route
          path="/notificaciones/gestion"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Notificaciones">
                <NotificationsContainer userRole={user?.role || 'director'} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/estadisticas"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Estadísticas">
                <StatisticsContainer userRole={user?.role || 'director'} />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Settings - Directors only */}
        <Route
          path="/configuracion"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Configuración">
                <SettingsContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Certificate Template Editor - Directors only */}
        <Route
          path="/configuracion/certificados/plantillas/nueva"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Certificados">
                <CertificateEditorContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/configuracion/certificados/plantillas/editor/:templateId"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Certificados">
                <CertificateEditorContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Subscription Management - Directors only */}
        <Route
          path="/configuracion/suscripcion"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <DirectorSubscriptionContainer />
            </ProtectedRoute>
          }
        />

        {/* Platform Settings - Directors only */}
        <Route
          path="/configuracion/plataforma"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Configuración">
                <PlatformSettingsContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Customization Settings - Directors only */}
        <Route
          path="/configuracion/personalizacion"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Configuración">
                <CustomizationSettingsContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* User Management - Directors only */}
        <Route
          path="/configuracion/usuarios"
          element={
            <ProtectedRoute allowedRoles={['director'] as UserRole[]}>
              <ErrorBoundary featureName="Configuración">
                <UserManagementContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Profile - All users */}
        <Route
          path="/perfil"
          element={
            <ProtectedRoute allowedRoles={['director', 'teacher', 'student'] as UserRole[]}>
              <ProfileContainer />
            </ProtectedRoute>
          }
        />

        {/* Workspace Management - Admin only */}
        <Route
          path="/workspaces"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <ErrorBoundary featureName="Gestión de Workspaces">
                <WorkspaceManagementContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        <Route
          path="/workspaces/:id"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <ErrorBoundary featureName="Gestión de Workspaces">
                <WorkspaceDetailContainer />
              </ErrorBoundary>
            </ProtectedRoute>
          }
        />

        {/* Client Management - Admin only */}
        <Route
          path="/clientes"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <ClientManagementContainer />
            </ProtectedRoute>
          }
        />

        {/* Subscription Plans - Admin only */}
        <Route
          path="/admin/planes"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <SubscriptionPlansContainer />
            </ProtectedRoute>
          }
        />

        {/* Cron Jobs - Admin only */}
        <Route
          path="/admin/cron-jobs"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <CronJobsContainer />
            </ProtectedRoute>
          }
        />

        {/* Payment Management - Admin only */}
        <Route
          path="/admin/pagos"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <PaymentDashboardContainer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/pagos/solicitudes"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <UpgradeRequestsContainer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/pagos/suscripciones"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <SubscriptionsContainer />
            </ProtectedRoute>
          }
        />

        <Route
          path="/admin/pagos/metodos"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <PaymentMethodConfigContainer />
            </ProtectedRoute>
          }
        />

        {/* Version History - Admin only */}
        <Route
          path="/admin/versiones"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <VersionHistoryContainer />
            </ProtectedRoute>
          }
        />

        {/* Email Management - Admin only */}
        <Route
          path="/admin/emails"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <EmailManagementContainer />
            </ProtectedRoute>
          }
        />

        {/* Pipeline Alerts - Admin only */}
        <Route
          path="/admin/pipeline-alerts"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <MuxWebhooksContainer />
            </ProtectedRoute>
          }
        />

        {/* Video Analytics - Admin only */}
        <Route
          path="/admin/video-analytics"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <AdminVideoAnalyticsContainer />
            </ProtectedRoute>
          }
        />

        {/* Workspace Alerts - Admin only */}
        <Route
          path="/admin/pagos/alertas"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <WorkspaceAlertsContainer />
            </ProtectedRoute>
          }
        />

        {/* App Config - Admin only */}
        <Route
          path="/admin/app-config"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <AppConfigContainer />
            </ProtectedRoute>
          }
        />

        {/* Logo Generator - Admin only */}
        <Route
          path="/admin/logo-generator"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <LogoGeneratorContainer />
            </ProtectedRoute>
          }
        />

        {/* Feature Flags - Admin only */}
        <Route
          path="/admin/feature-flags"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <FeatureFlagContainer />
            </ProtectedRoute>
          }
        />

        {/* System Architecture - Admin only */}
        <Route
          path="/admin/system-architecture"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <SystemArchitectureContainer />
            </ProtectedRoute>
          }
        />

        {/* Flashcard Analytics - Admin only */}
        <Route
          path="/admin/flashcard-analytics"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <FlashcardAnalyticsContainer />
            </ProtectedRoute>
          }
        />

        {/* Herramientas Hub - Admin only */}
        <Route
          path="/admin/herramientas"
          element={
            <ProtectedRoute allowedRoles={['admin'] as UserRole[]}>
              <HerramientasContainer />
            </ProtectedRoute>
          }
        />

        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </PageTransition>
    </Suspense>
  );
}
